// Route: /materials/[id] — workspace материала. Lazy split.
// URL: ?tab=note|flashcards|quiz|tutor|glossary|recording|audio|extracted|source
//      ?demo=loading|empty|error|processing
//
// См. docs/ui-spec/materials.[id].{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { WORKSPACE_PAGE_SHELL } from "@studyhub/ui";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { z } from "zod";
import { LazyRouteBoundary } from "@/components/shared/lazy-route-boundary";
import { useTrackView } from "@/hooks/use-track-view";
import { fromSearchSchema } from "@/lib/back-from";
import { cn } from "@/lib/cn";

const MaterialWorkspacePage = lazy(() => import("@/components/materials/material-workspace-page"));

const searchSchema = z
	.object({
		tab: z
			.enum([
				"note",
				"flashcards",
				"quiz",
				"tutor",
				"glossary",
				"voice",
				"recording",
				"audio",
				"extracted",
				"source",
			])
			.optional(),
		demo: z.enum(["default", "loading", "empty", "error", "processing", "offline"]).optional(),
	})
	.merge(fromSearchSchema);

export const Route = createFileRoute("/materials/$materialId")({
	validateSearch: tolerantSearch(searchSchema),
	component: MaterialWorkspaceRoute,
});

function MaterialWorkspaceRoute(): React.ReactElement {
	const { materialId } = useParams({ from: "/materials/$materialId" });
	useTrackView("material", materialId);
	return (
		<LazyRouteBoundary>
			<Suspense fallback={<WorkspaceSkeleton />}>
				<MaterialWorkspacePage />
			</Suspense>
		</LazyRouteBoundary>
	);
}

function WorkspaceSkeleton(): React.ReactElement {
	return (
		<div className={cn(WORKSPACE_PAGE_SHELL, "space-y-4")}>
			<Skeleton className="h-14 w-full rounded-[var(--radius-panel)]" />
			<Skeleton className="h-11 w-2/3 rounded-[var(--radius-md)]" />
			<div className="grid gap-4 lg:grid-cols-[1fr_var(--rail-width)]">
				<Skeleton className="h-[70dvh] rounded-[var(--radius-panel)]" />
				<Skeleton className="h-[70dvh] rounded-[var(--radius-panel)]" />
			</div>
		</div>
	);
}
